/*
 * Header simply displays the header on all pages
 */

import * as React from 'react';
import HeaderMenu from './HeaderMenu';
import { Link } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import HeaderFacilitySelector from './HeaderFacilitySelector';
import { getUser } from '../../reducers/manageUserReducer';
import CountrySelector from './CountrySelector';
import { useState, useEffect, useMemo } from 'react';
import { Icountry } from '../../models';
import { getCountries } from '../../reducers/countriesReducer';
import { loadCountries } from '../../actions/countriesActions';
import { clearSelectedFacilityID } from '../../actions/commonActions';
import { constants } from '../../constants/constants';
import * as localForage from 'localforage';
import * as types from '../../actions/actionTypes';
import headerImg from '../../images/BeaconLogo-april-2025.png';

// LocalForage key for persisting selected country
const SELECTED_COUNTRY_STORAGE_KEY = 'header.selectedCountry';

const Header = (props: any) => {
    const user = useSelector(getUser);
    const allCountries = useSelector(getCountries);
    const dispatch = useDispatch();
    const [selectedCountry, setSelectedCountry] = useState<
        Icountry | undefined
    >(user.country);

    // Load countries from backend if needed (replicating EditUserForm logic)
    useEffect(() => {
        if (user.id.length) {
            loadCountries(dispatch, allCountries);
        }
    }, [dispatch, allCountries, user.id]);

    // Process user countries the same way EditUserForm does
    const userForCountrySelector = useMemo(() => {
        // Replicate editUserItemToFormValues logic for country processing
        const { countryID, userCountries: countries } = user;

        // Check if user has admin permissions that might give them access to all countries
        const hasAllCountriesAccess = countries && countries.length > 50; // Assume 50+ means "all countries"

        let selectedCountries: string[];
        if (hasAllCountriesAccess) {
            // For admin users, derive their personal countries from their assigned facilities
            // This matches the logic in SelectFacilityForm where facilities determine actual assignments
            const facilitiesCountries = [
                ...new Set(
                    user.facilities?.map(f => f.countryID).filter(Boolean) || []
                )
            ];

            if (facilitiesCountries.length > 0) {
                selectedCountries = facilitiesCountries;
                console.log(
                    '🔧 Admin Override: Using countries from facilities:',
                    facilitiesCountries,
                    'instead of all',
                    countries.length,
                    'countries'
                );
            } else {
                // Fallback to primary country if no facilities
                selectedCountries = [countryID];
                console.log(
                    '🔧 Admin Override: No facilities found, using primary country only'
                );
            }
        } else {
            // Normal user - use their assigned countries or fallback to primary
            selectedCountries =
                !countries || countries.length === 0 ? [countryID] : countries;
        }

        let countryOptions: string[] = [];

        if (allCountries.length) {
            // Use backend countries if available
            countryOptions = selectedCountries
                .map(sc => {
                    const fullCountry = allCountries.find(c => c.id === sc);
                    return fullCountry?.id;
                })
                .filter(id => id) as string[]; // Filter out undefined entries
        } else {
            // Fallback to constants.countries - just return the valid IDs
            countryOptions = selectedCountries.filter(
                c => constants.countries[c]
            ); // Only include valid country IDs
        }

        return {
            ...user,
            userCountries: countryOptions // Already string array
        };
    }, [user, allCountries]);

    // Load and restore saved country selection on login/user change
    useEffect(() => {
        const loadSavedCountry = async () => {
            if (!user.id) return;

            try {
                // Try to load saved country from localforage
                const savedCountryId = await localForage.getItem<string>(
                    `${SELECTED_COUNTRY_STORAGE_KEY}.${user.id}`
                );

                if (savedCountryId && typeof savedCountryId === 'string') {
                    // Verify the saved country is still valid for this user
                    const userCountries =
                        userForCountrySelector.userCountries || [];
                    const isValidCountry = userCountries.includes(
                        savedCountryId
                    );

                    if (isValidCountry) {
                        // Restore saved country selection
                        dispatch({
                            type: types.UPDATE_USER_LOCAL_COUNTRY,
                            payload: savedCountryId
                        });

                        // Update local state
                        const savedCountry =
                            constants.countries[savedCountryId];
                        if (savedCountry) {
                            setSelectedCountry(savedCountry);
                        }
                    }
                } else {
                    // No saved country - set default to primary country if available
                    const userCountries =
                        userForCountrySelector.userCountries || [];
                    const defaultCountryId = user.countryID;

                    if (userCountries.includes(defaultCountryId)) {
                        dispatch({
                            type: types.UPDATE_USER_LOCAL_COUNTRY,
                            payload: defaultCountryId
                        });
                    }
                }
            } catch (error) {
                console.warn('Failed to load saved country selection:', error);
            }
        };

        loadSavedCountry();
        // eslint-disable-next-line
    }, [user.id, userForCountrySelector.userCountries, dispatch]);

    const handleCountryChange = async (country: Icountry) => {
        setSelectedCountry(country);

        // Clear selected facility when country changes to avoid showing data from wrong country
        dispatch(clearSelectedFacilityID());

        // Clear service grid data to prevent showing stale data
        dispatch({
            type: types.GET_DASHBOARD_INSPECTION_STATUS_SUCCESS,
            payload: null
        });
        dispatch({
            type: types.GET_DASHBOARD_MAINTENANCE_ACTIONS_SUCCESS,
            payload: null
        });
        dispatch({
            type: types.GET_DASHBOARD_WORK_HISTORY_SUCCESS,
            payload: null
        });

        dispatch({
            type: types.UPDATE_USER_LOCAL_COUNTRY,
            payload: country.value
        });

        try {
            await localForage.setItem(
                `${SELECTED_COUNTRY_STORAGE_KEY}.${user.id}`,
                country.value
            );
        } catch (error) {
            console.warn('Failed to save country selection:', error);
        }
    };

    const handleHeaderClick = (e: React.MouseEvent) => {
        // React 16 portal events bubble through the React component tree (not the DOM tree),
        // so clicks inside the modal — including on the backdrop div — reach this handler.
        // If the click already came from inside the modal, bail out: the modal's own
        // handleBackdropClick already called onHide, and triggering .click() here again
        // would cause a second dispatch that re-opens the modal (MMG-3972).
        const target = e.target as HTMLElement;
        if (
            target.classList.contains('modal-backdrop') ||
            !!(target as Element).closest?.('.modal')
        ) {
            return;
        }
        const backdrop = document.querySelector('.modal-backdrop');
        if (backdrop) {
            (backdrop as HTMLElement).click();
        }
    };

    return (
        <div className="header" onClick={handleHeaderClick}>
            <div className="header-left">
                <Link to={'/dashboard'}>
                    <img src={headerImg} alt="logo" />
                </Link>

                {user && user.id ? (
                    <>
                        <div className="header-country-selector">
                            <CountrySelector
                                selectedCountry={selectedCountry}
                                onCountryChange={handleCountryChange}
                                user={userForCountrySelector}
                            />
                        </div>
                        <span className="header-divider" />
                        <HeaderFacilitySelector />
                    </>
                ) : null}
            </div>

            {/* Desktop and Mobile - use same mega menu */}
            <HeaderMenu {...props} />
        </div>
    );
};

export default Header;
